import React, { Component } from 'react';
import { withTheme, withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import clsx from 'clsx';

// ui/ux
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

// ours
import { uuidv4 } from '../../../../common';

import BarChart from '../../../details/BarChart';
import ThumbsChart from '../../../details/ThumbsChart';
import PhotoChart from '../../../details/PhotoChart';
import MSPieChart from '../../../details/MSPieChart';

// modules
const deepEqual = require('deep-equal');

const PERMITTED_HAND_MAPPINGS = ['yes', 'no', 'other', 'unsure'];

const BAR = 0,
  HANDS = 1,
  PHOTO = 2,
  MSPIE = 3,
  // TEXT = 4,
  // GEO = 5,
  HISTO = 6;

const PHOTO_DEMO_URLS = [
  'https://storage.googleapis.com/prod-premise-android-observation-us/1fa12b54-699c-4203-8278-b2170b394ec9.jpg',
  'https://storage.googleapis.com/prod-premise-android-observation-us/4fbd45dc-f595-40c2-80b3-3dea26783c26.jpg',
  'https://storage.googleapis.com/prod-premise-android-observation-us/8f06ea6a-c7b1-45c9-964c-15c8e82c624e.jpg',
  'https://storage.googleapis.com/prod-premise-android-observation-us/e717d749-4477-4e7b-a226-32be08af3060.jpg',
  'https://storage.googleapis.com/prod-premise-android-observation-us/9f839c26-989a-4fe0-b2c3-09b3f414b4c3.jpg',
  'https://storage.googleapis.com/prod-premise-android-observation-us/24e27d27-6e7b-47cb-9b4f-c6b27941ca7b.jpg',
  'https://storage.googleapis.com/prod-premise-android-observation-us/43531025-0142-4979-b7da-441db9ee5a35.jpg',
  'https://storage.googleapis.com/prod-premise-android-observation-us/ca073301-0819-4a78-9c90-f97528fb5f67.jpg',
  'https://storage.googleapis.com/prod-premise-android-observation-us/0a71d37d-e5b4-4358-b102-7f8a7d1ed5ad.jpg',
  'https://storage.googleapis.com/prod-premise-android-observation-us/6a5b6621-5562-44be-991e-aee7c0a488a9.jpg'
];

const GRAPH_APPLICABLE = [
  ['SELECT_ONE'], // BAR
  ['SELECT_ONE'], // HANDS
  ['PHOTO', 'SCREENSHOT'], // PHOTO
  ['SELECT_MANY'], // MSPIE
  ['TEXT'], // TEXT
  ['GEOPOINT'], // GEO
  ['NUMBER'] // HISTO
];

const defaultForInputType = (input_type, opts) => {
  for (let i = 0; i < GRAPH_APPLICABLE.length; i++) {
    if (GRAPH_APPLICABLE[i].includes(input_type)) {
      if (input_type === 'SELECT_ONE' && opts.map((d) => d.value).includes('yes')) {
        return HANDS;
      } else {
        return i;
      }
    }
  }
};

const styles = (theme) => ({
  gutterBottom: {
    marginBottom: theme.spacing(2)
  },
  input: {
    '& .MuiFormLabel-root.Mui-focused': {
      color: theme.palette.text.secondary
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.secondary
    }
  },
  fullWidth: {
    width: '100%'
  },
  chartDemo: {
    maxWidth: 400,
    overflow: 'hidden'
  },
  createAggGrid: {
    marginTop: theme.spacing(3)
  },
  excerpt: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.getContrastText(theme.palette.primary.main),
    fontWeight: 'bold'
  }
});

class CreateAggregation extends Component {
  constructor(props) {
    super(props);
    console.log('CreateAggregation', JSON.stringify({ ...props, classes: null, theme: null, onComplete: null }));
    const { input_type, labels, opts } = props.formQuestion;
    this._inputRefs = {};
    const friendlyName = labels && labels.length > 0 ? labels[0] : '';
    this.state = {
      id: uuidv4(),
      // ui/ux
      aggTypeSelectedIdx: defaultForInputType(input_type, opts),
      friendlyName,
      friendlyNameError: this.friendlyNameError(friendlyName, props),
      errors: {},
      formValid: false,
      usedDisplayKeys: this.cleanUsedDisplayKeys(props.usedDisplayKeys)
    };

    // select distinct (jsonb_each(aggregations)).value->'aggregator' from places;
    this._availableGraphics = [
      { k: 'Bar Chart', v: 'bar_aggregator' },
      { k: 'Hands Chart', v: 'hands_aggregator' },
      { k: 'Photo List', v: 'photo_list_aggregator' },
      { k: 'Multiselect Pie Chart', v: 'ms_pie_aggregator' },
      { k: 'Text List', v: 'text_aggregator' },
      { k: 'Geo List', v: 'geo_aggregator' },
      { k: 'Histogram Chart', v: 'numeric_aggregator' }
      // { k: '', v: 'demo_aggregator' }
    ];
    this.next = this.next.bind(this);
    this.skip = this.skip.bind(this);
    this.onRadioChange = this.onRadioChange.bind(this);
    this.onFriendlyNameChange = this.onFriendlyNameChange.bind(this);
  }
  componentDidMount() {
    this.componentDidUpdate(this.props);
  }

  // This function prevents componentDidUpdate()
  // from firing needlessly when configuring
  // error conditions for forms
  //
  // TODO:
  // This should be refactored so that component
  // update routines care for error. :|
  safeSetState(newState) {
    const existingState = {};
    for (const k in newState) {
      existingState[k] = this.state[k];
    }
    if (!deepEqual(newState, existingState)) {
      return this.setState(newState);
    }
  }
  cleanUsedDisplayKeys(usedDisplayKeys) {
    return usedDisplayKeys.map((d) => d.toLowerCase().trim());
  }
  friendlyNameError(friendlyName, props) {
    const usedDisplayKeys = props ? this.cleanUsedDisplayKeys(props.usedDisplayKeys) : this.state.usedDisplayKeys;
    if (friendlyName.length === 0) {
      return 'Field Required';
    } else if (usedDisplayKeys.includes(friendlyName)) {
      return 'You cannot reuse display keys';
    } else {
      return null;
    }
  }
  componentDidUpdate(prevProps) {
    const { formQuestion, usedDisplayKeys } = this.props;
    if (formQuestion !== prevProps.formQuestion) {
      const { input_type, labels } = formQuestion;
      const friendlyName = labels && labels.length > 0 ? labels[0] : '';
      return this.setState({
        usedDisplayKeys: this.cleanUsedDisplayKeys(usedDisplayKeys),
        friendlyName,
        friendlyNameError: this.friendlyNameError(friendlyName, this.props),
        aggTypeSelectedIdx: defaultForInputType(input_type, formQuestion.opts),
        errors: {},
        formValid: false
      });
    }

    const { friendlyName, aggTypeSelectedIdx } = this.state;
    const friendlyNameError = this.friendlyNameError(friendlyName);
    let failed = !!friendlyNameError || aggTypeSelectedIdx === null;

    const errors = {};
    if (aggTypeSelectedIdx !== HANDS) {
      this.safeSetState({
        formValid: !failed,
        friendlyNameError,
        errors
      });
    } else {
      // do extra validation for "HANDS" mapping:
      for (const value in this._inputRefs) {
        const curMapping = this._inputRefs[value].input.value.toLowerCase();
        if (!PERMITTED_HAND_MAPPINGS.includes(curMapping)) {
          errors[value] = `All inputs must map to one of ${PERMITTED_HAND_MAPPINGS.join(', ')}`;
          failed = true;
        }
      }

      this.safeSetState({
        formValid: !failed,
        friendlyNameError,
        errors
      });
    }
  }
  skip() {
    this.props.onComplete(null /* must have a return value for form/topic render delegation */);
  }
  renderButtons(nextEnabled) {
    return (
      <Grid container justifyContent="flex-end" spacing={2}>
        <Grid item>
          <Button variant="outlined" color="secondary" onClick={this.skip}>
            Skip
          </Button>
        </Grid>

        <Grid item>
          <Button disabled={!nextEnabled} variant="outlined" color="secondary" onClick={this.next}>
            Next
          </Button>
        </Grid>
      </Grid>
    );
  }
  onRadioChange(e) {
    this.setState({
      aggTypeSelectedIdx: +e.target.value
    });
  }
  next() {
    const { onComplete, formQuestion } = this.props;
    const { aggTypeSelectedIdx, friendlyName } = this.state;

    const answerMappings = Object.keys(this._inputRefs).map((d) => {
      let label = this._inputRefs[d].input.value;
      if (aggTypeSelectedIdx === HANDS) {
        label = label.toLowerCase();
      }
      return { value: d, label };
    });

    onComplete({ formQuestion, newAggMapping: { aggType: this._availableGraphics[aggTypeSelectedIdx].v, name: friendlyName, mappings: answerMappings } });
  }
  renderDemo() {
    const { aggTypeSelectedIdx, friendlyName } = this.state;
    const commonProps = {
      title: friendlyName || 'User Friendly Title',
      width: 400
    };
    if (aggTypeSelectedIdx === BAR) {
      return (
        <BarChart
          data={{
            cat: 20,
            dog: 40,
            moose: 1
          }}
          {...commonProps}
        />
      );
    } else if (aggTypeSelectedIdx === HANDS) {
      return <ThumbsChart data={{ yes: 20, no: 10, other: 5 }} {...commonProps} />;
    } else if (aggTypeSelectedIdx === PHOTO) {
      return <PhotoChart data={PHOTO_DEMO_URLS} {...commonProps} />;
    } else if (aggTypeSelectedIdx === MSPIE) {
      return (
        <MSPieChart
          data={{
            cat: 20,
            dog: 40,
            moose: 1
          }}
          {...commonProps}
        />
      );
    } else if (aggTypeSelectedIdx === HISTO) {
      return (
        <BarChart
          data={{
            10: 10,
            20: 20,
            30: 30
          }}
          {...commonProps}
        />
      );
    } else {
      return null;
    }
  }
  onFriendlyNameChange(e) {
    const friendlyName = e.target.value;
    this.setState({ friendlyName });
  }
  renderRewrite() {
    const { classes, formQuestion } = this.props;
    const { errors } = this.state;
    const errorKeys = Object.keys(errors);

    if (!formQuestion.opts || formQuestion.opts.length === 0) {
      return null;
    }

    return (
      <Grid item className={classes.fullWidth}>
        <Typography variant="h6">Optionally, rewrite answers.</Typography>
        <Typography component="div" variant="caption" className={classes.gutterBottom}>
          This key will propagate to the user interface and should be user friendly.
        </Typography>
        <Grid container direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2} className={classes.fullWidth}>
          {formQuestion.opts.map((d) => (
            <React.Fragment key={d.value}>
              <Grid item className={classes.fullWidth}>
                <Typography className={classes.gutterBottom}>{d.value}</Typography>
                <TextField
                  helperText={errorKeys.includes(d.value) ? errors[d.value] : ''}
                  error={errorKeys.includes(d.value)}
                  ref={(r) => {
                    this._inputRefs[d.value] = this._inputRefs[d.value] || {};
                    this._inputRefs[d.value].field = r;
                  }}
                  InputProps={{
                    inputRef: (r) => {
                      this._inputRefs[d.value] = this._inputRefs[d.value] || {};
                      this._inputRefs[d.value].input = r;
                    }
                  }}
                  onChange={() => {
                    this.forceUpdate();
                  }}
                  className={classes.input}
                  fullWidth
                  variant="outlined"
                  defaultValue={d.label}
                />
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Grid>
    );
  }
  render() {
    const { classes, topic, formQuestion } = this.props;
    const { id, aggTypeSelectedIdx, friendlyName, formValid, friendlyNameError } = this.state;
    const { input_type, labels } = formQuestion;

    this._inputRefs = {};
    return (
      <React.Fragment>
        <Typography>
          {'Create a new aggregation for the question, of type "'}
          <span className={classes.excerpt}>{input_type}</span>
          {'", "'}
          <span className={classes.excerpt}>{labels.join(', ')}</span>
          {'" for the topic "'}
          <span className={classes.excerpt}>{topic}</span>
          {'".'}
        </Typography>
        {this.renderButtons(formValid)}
        <Grid container direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2} className={clsx(classes.createAggGrid, classes.gutterBottom)}>
          <Grid item>
            <Typography variant="h6" className={classes.gutterBottom}>
              Enter a user friendly title that will propagate to the user as a header for the graphic:
            </Typography>
            <Grid item container direction="row" justifyContent="center" alignItems="center" spacing={2}>
              <Grid item xs>
                <TextField
                  error={!!friendlyNameError}
                  helperText={friendlyNameError ? friendlyNameError : ''}
                  className={classes.input}
                  fullWidth
                  label="User Friendly Title"
                  variant="outlined"
                  placeholder="🏷️ Does this clinic have immunizations?"
                  value={friendlyName}
                  onChange={this.onFriendlyNameChange}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Typography variant="h6" className={classes.gutterBottom}>
              Choose an aggregation type:
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup id={`rg-aggtype-${id}`} value={aggTypeSelectedIdx} name="rg-aggtype" onChange={this.onRadioChange} row>
                {this._availableGraphics.map((d, i) => (
                  <FormControlLabel key={d.v} value={i} control={<Radio />} label={d.k} disabled={!GRAPH_APPLICABLE[i].includes(input_type)} />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item className={classes.fullWidth}>
            <Grid container direction="row" justifyContent="center" alignItems="center">
              <Grid item className={classes.chartDemo}>
                {this.renderDemo()}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {this.renderRewrite()}
        {this.renderButtons(formValid)}
      </React.Fragment>
    );
  }
}

CreateAggregation.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,

  usedDisplayKeys: PropTypes.array,
  formQuestion: PropTypes.object.isRequired,
  topic: PropTypes.string.isRequired,
  selectedForm: PropTypes.object.isRequired,

  onComplete: PropTypes.func.isRequired
};

CreateAggregation.defaultProps = {
  usedDisplayKeys: []
};

export default withTheme(withStyles(styles)(CreateAggregation));
